import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useRouter } from 'next/router';
import {
  Heading,
  HeadingEnum,
  BoldEnum,
  TextColorEnum,
  SideSheet,
  Switch,
  Text,
  TextEnum,
  Input,
  Button,
  LabelEnum,
  Coupon,
  Image,
  ImageSizeEnum,
} from '@desquared/wave-components-library';
import { daysUntilExpires, formatExpiresDate } from '@/utils/dates/dates.js';
import { CouponAdditionSideSheetOpen } from '@/store/sideSheet';
import CouponsSideSheetEmptyState from '../../../public/assets/wave-grocery/Empty-States-Default/CouponsSideSheetEmptyState.png';
import { useToast } from '@/contexts/ToastContext';
import { useTranslations } from 'next-intl';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { addCoupon, getCoupons } from '@/api/wave/users';
import { Controller, useForm } from 'react-hook-form';
import styles from './CouponsSideSheet.module.css';

export default function CouponsSideSheet({ user, selectedCoupon, setCoupon, handleCheckCoupon }) {
  const [addCouponSwitch, setAddCouponSwitch] = useState(!!selectedCoupon);
  const [localCoupon, setLocalCoupon] = useState('');
  const [sideSheet, setSideSheet] = useState(false);
  const refOne = useRef(null);
  const [newlyAddedCode, setNewlyAddedCode] = useState(null);
  const { locale } = useRouter();
  const statusesMap = {
    active: LabelEnum.Warning,
    redeemed: LabelEnum.Open,
    expired: LabelEnum.Close,
  };
  const queryClient = useQueryClient();
  const t = useTranslations();
  const { showToast } = useToast();

  useEffect(() => {
    setLocalCoupon(selectedCoupon || '');
  }, [selectedCoupon, sideSheet]);

  useEffect(() => {
    const CouponAdditionSideSheetOpen$ = CouponAdditionSideSheetOpen.subscribe(isOpen => {
      setSideSheet(isOpen);
      if (isOpen) {
        refetch();
      }
    });
    document.addEventListener('click', handleClickOutside, true);

    return () => {
      CouponAdditionSideSheetOpen$.unsubscribe();

      document.removeEventListener('click', handleClickOutside, false);
    };
  }, []);

  const handleClickOutside = e => {
    if (refOne.current && !refOne.current.contains(e.target)) {
      CouponAdditionSideSheetOpen.close();
    }
  };

  const addCouponMutation = useMutation({
    mutationFn: payload => addCoupon(user.token, payload),
    onSuccess: (data, variables) => {
      setNewlyAddedCode(variables.code);
      queryClient.invalidateQueries({ queryKey: ['user', 'coupons', locale] });

      showToast(t('coupons.sidesheet.added_coupon'));
    },
    onError: error => {
      const status = error?.response?.status;
      if (status === 404) {
        setError('code', {
          type: 'server',
          message: t('coupons.sidesheet.errors.notFound'),
        });
        return;
      }
      setError('code', {
        type: 'server',
        message: t('coupons.sidesheet.errors.notFound'),
      });
    },
  });

  const {
    control,
    handleSubmit,
    reset,
    setError,
    clearErrors,
    formState: { errors, isValid, isSubmitting },
  } = useForm({
    mode: 'onChange',
    defaultValues: { code: '' },
  });

  const handleClickCoupon = code => {
    const clickedCode = code.text;
    setLocalCoupon(prev => (prev === clickedCode ? '' : clickedCode));
  };

  useEffect(() => {
    if (addCouponSwitch) {
      reset({ code: '' });
      clearErrors();
    }
  }, [addCouponSwitch, reset, clearErrors]);

  const onSubmit = values => {
    const code = values.code.trim();
    if (!code) {
      setError('code', { type: 'validate', message: t('coupons.sidesheet.errors.required') });
      return;
    }
    addCouponMutation.mutate({ code });
  };

  const selectCoupons = useCallback(
    response => {
      if (!response.data) return [];
      return response.data
        .filter(coupon => coupon.status === 'active')
        .map(coupon => {
          const daysLeft = daysUntilExpires(coupon.expiresAt);
          const isExpiringSoon = daysLeft >= 0 && daysLeft < 3;
          const isExpiringToday = daysLeft === 0;
          const isNew = coupon.code === newlyAddedCode;
          let open = undefined;
          let closed = undefined;
          let closingSoon = undefined;

          if (coupon.status === 'active' && isNew) {
            open = t('coupons.sidesheet.new_coupon');
            closingSoon = undefined;
          }
          if (coupon.status === 'active' && !isNew) {
            closingSoon = isExpiringToday
              ? t('coupons.expires')
              : isExpiringSoon && daysLeft !== null
              ? `${t('coupons.expire_in')} ${daysLeft} ${t('coupons.expire_in_days', { count: daysLeft })}`
              : undefined;
          }

          if (coupon.status === 'redeemed' && !isNew) {
            open = t('coupons.redeemed', { count: 1 });
          }

          if (coupon.status === 'expired' && !isNew) {
            closed = t('coupons.expired', { count: 1 });
          }

          return {
            _id: coupon._id,
            title: coupon.name,
            subtitle: formatExpiresDate(coupon.expiresAt, t('coupons.no_expiration')),
            description: coupon.description,
            code: { text: coupon.code, status: 'success' },
            onClickCoupon: undefined,
            status: isNew ? statusesMap['redeemed'] : statusesMap[coupon.status],
            openStoreLabelWording: open,
            closedStoreLabelWording: closed,
            closingSoonStoreLabelWording: closingSoon,
          };
        });
    },
    [newlyAddedCode, t, selectedCoupon]
  );

  const { data: coupons, refetch } = useQuery({
    queryKey: ['user', 'coupons', locale, sideSheet],
    queryFn: () => getCoupons(user.token),
    enabled: !!user?.token,
    onError: console.log,
    select: selectCoupons,
  });

  const onClick = () => {
    setCoupon(localCoupon);
    handleCheckCoupon(localCoupon);
    setTimeout(() => {
      CouponAdditionSideSheetOpen.close();
    }, '300');
  };

  const closeSwitch = value => {
    setAddCouponSwitch(value);
    setCoupon('');
  };

  if (!sideSheet) return null;

  return (
    <div ref={refOne}>
      <SideSheet
        buttonText={t('coupons.sidesheet.continue')}
        header={t('coupons.sidesheet.available_coupons')}
        hasClose={true}
        onSubmit={onClick}
        position={'left'}
        isInFullEdit={false}
        isWide={false}
      >
        <div className={styles.container}>
          <div className={styles.labelContainer}>
            <div className={styles.label}>
              <Text type={TextEnum.Body}>{t('coupons.sidesheet.use_coupons')}</Text>
            </div>
            <Switch selected={addCouponSwitch} setSelected={closeSwitch} />
          </div>
          {addCouponSwitch && (
            <div>
              <Heading type={HeadingEnum.HeadingS} bold={BoldEnum.Bold} classOption={styles.headingContainer}>
                {t('coupons.sidesheet.add_coupon')}
              </Heading>
              <div className={styles.inputContainer}>
                <Controller
                  control={control}
                  name="code"
                  rules={{
                    required: t('coupons.sidesheet.errors.required'),
                    validate: value => value.trim().length > 0 || t('coupons.sidesheet.errors.required'),
                  }}
                  render={({ field }) => (
                    <>
                      <Input
                        {...field}
                        value={field.value}
                        onChange={field.onChange}
                        name={field.name}
                        helperTextType={errors?.code ? 'error' : undefined}
                        errorText={errors.code ? errors.code?.message : ''}
                        label={t('coupons.sidesheet.placeholder')}
                      />
                    </>
                  )}
                />
                <Button variant="tonal" text={t('coupons.add')} onClick={handleSubmit(onSubmit)} />
              </div>
              <Heading type={HeadingEnum.HeadingS} bold={BoldEnum.Bold} classOption={styles.headingContainer}>
                {t('coupons.sidesheet.my_coupons')}
              </Heading>
              <div className={styles.couponsContainer}>
                {coupons?.length ? (
                  coupons.map((coupon, index) => (
                    <Coupon
                      key={coupon?._id || `couponId${index}`}
                      title={coupon.title}
                      subtitle={coupon.subtitle}
                      description={coupon.description}
                      code={coupon.code}
                      badge={coupon.badge}
                      status={coupon.status}
                      openStoreLabelWording={coupon.openStoreLabelWording}
                      closedStoreLabelWording={coupon.closedStoreLabelWording}
                      closingSoonStoreLabelWording={coupon.closingSoonStoreLabelWording}
                      onClickCoupon={() => handleClickCoupon(coupon.code)}
                      isSelected={coupon.code.text === localCoupon}
                    />
                  ))
                ) : (
                  <div className={styles.emptyState}>
                    <Image
                      size={ImageSizeEnum.CartEmptyState}
                      imgSrc={CouponsSideSheetEmptyState.src}
                      imgTitle={'empty-state'}
                      alt={'Empty State'}
                    />
                    <Text type={TextEnum.Assistive} color={TextColorEnum.OnSurfaceLowEmphasis} bold={BoldEnum.Bold}>
                      {t('coupons.sidesheet.no_active_coupons')}
                    </Text>

                    <Text type={TextEnum.Assistive} color={TextColorEnum.OnSurfaceLowEmphasis}>
                      {t('coupons.sidesheet.code_prompt')}
                    </Text>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </SideSheet>
    </div>
  );
}
